/*
MIT license
Moved into our repo for maintainability and better deps management.
Forked from: https://github.com/alexreardon/memoize-one
*/

// Using ReadonlyArray<T> rather than readonly T as it works with TS v3
type EqualityFn = (newArgs: any[], lastArgs: any[]) => boolean

function areInputsEqual(newInputs: readonly unknown[], lastInputs: readonly unknown[]): boolean {
  // no checks needed if the inputs length has changed
  if (newInputs.length !== lastInputs.length) {
    return false
  }

  for (let i = 0; i < newInputs.length; i++) {
    // using shallow equality check
    if (newInputs[i] !== lastInputs[i]) {
      return false
    }
  }
  return true
}

export const memoizeOne = <
  // Need to use 'any' rather than 'unknown' here as it has he correct Generic narrowing behavior.
  ResultFn extends (this: any, ...newArgs: any[]) => ReturnType<ResultFn>,
>(
  resultFn: ResultFn,
  isEqual: EqualityFn = areInputsEqual,
): ResultFn => {
  let lastThis: unknown
  let lastArgs: unknown[] = []
  let lastResult: ReturnType<ResultFn>
  let calledOnce: boolean = false

  // breaking cache when context (this) or arguments change
  function memoized(this: unknown, ...newArgs: unknown[]): ReturnType<ResultFn> {
    if (calledOnce && lastThis === this && isEqual(newArgs, lastArgs)) {
      return lastResult
    }

    // Throwing during an assignment aborts the assignment
    // Doing the lastResult assignment first so that if it throws nothing will be overwritten
    lastResult = resultFn.apply(this, newArgs)
    calledOnce = true
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this
    lastArgs = newArgs

    return lastResult
  }

  return memoized as ResultFn
}
